import { useState, useEffect } from 'react';
import { api } from '../api/client';
import { useAuth } from '../hooks/useAuth';

interface Beneficiary {
  id: string;
  name: string;
  email: string;
  relationship: string;
  role: 'full_access' | 'professional_only' | 'view_only';
  status: 'verified' | 'pending';
}

interface LegacyPlan {
  enabled: boolean;
  title: string;
  beneficiaries: Beneficiary[];
  triggers: {
    mode: 'inactivity' | 'time_locked' | 'emergency';
    inactivity_days: number;
    time_locked_date: string;
    emergency_dual_approval: boolean;
  };
  privacy_shield: {
    anonymize_sensitive: boolean;
    exclude_financial_vaults: boolean;
    strip_passwords_and_keys: boolean;
  };
  updated_at?: string;
}

export function LegacyPage() {
  const { tenant } = useAuth();
  const [plan, setPlan] = useState<LegacyPlan | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Beneficiary Modal
  const [showAddBenModal, setShowAddBenModal] = useState(false);
  const [benForm, setBenForm] = useState({
    name: '',
    email: '',
    relationship: 'Thành viên gia đình',
    role: 'full_access' as Beneficiary['role']
  });

  useEffect(() => {
    loadPlan();
  }, []);

  const loadPlan = async () => {
    setLoading(true);
    try {
      const res = await api.getLegacyPlan();
      setPlan(res);
    } catch (e) {
      console.error('Failed to load legacy plan', e);
    } finally {
      setLoading(false);
    }
  };

  const handleSavePlan = async () => {
    if (!plan) return;
    setSaving(true);
    try {
      await api.saveLegacyPlan(plan);
      alert('💾 Đã lưu thiết lập Di Chúc Tri Thức Số thành công!');
    } catch (e: any) {
      alert('Lỗi lưu kế hoạch: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleAddBeneficiary = (e: React.FormEvent) => {
    e.preventDefault();
    if (!plan) return;
    if (!benForm.name.trim() || !benForm.email.trim()) {
      alert('Vui lòng nhập họ tên và email người thừa kế.');
      return;
    }

    const newBen: Beneficiary = {
      id: `ben_${Date.now()}`,
      name: benForm.name.trim(),
      email: benForm.email.trim(),
      relationship: benForm.relationship,
      role: benForm.role,
      status: 'pending'
    };

    setPlan({
      ...plan,
      beneficiaries: [...plan.beneficiaries, newBen]
    });

    setShowAddBenModal(false);
    setBenForm({
      name: '',
      email: '',
      relationship: 'Thành viên gia đình',
      role: 'full_access'
    });
  };

  const handleRemoveBeneficiary = (id: string) => {
    if (!plan) return;
    if (confirm('Bạn có chắc chắn muốn xóa người thừa kế này?')) {
      setPlan({
        ...plan,
        beneficiaries: plan.beneficiaries.filter(b => b.id !== id)
      });
    }
  };

  const handleExportVault = async () => {
    setExporting(true);
    try {
      const res = await api.exportLegacyVault();
      if (res.success && res.package) {
        // Trigger browser download
        const blob = new Blob([JSON.stringify(res.package, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = res.archive_name || 'clean_vault_legacy_v1.json';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        alert(`✅ Đã xuất bản sao thừa kế sạch thành công!\n\nTệp tin: ${res.archive_name}\nTài liệu: ${res.package.summary?.document_count || 0} docs\nTrạng thái: Đã làm sạch bởi Privacy Guard`);
      }
    } catch (e: any) {
      alert('Lỗi khi xuất bản sao: ' + e.message);
    } finally {
      setExporting(false);
    }
  };

  if (loading || !plan) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', flexDirection: 'column', gap: '16px' }}>
        <div className="spinner" style={{ width: 32, height: 32 }} />
        <span style={{ color: 'var(--text-muted)', fontSize: '13px' }}>Đang nạp Di Chúc Tri Thức Số (Digital Knowledge Will)...</span>
      </div>
    );
  }

  return (
    <div style={{ padding: '28px 32px', maxWidth: '1300px', margin: '0 auto' }}>
      {/* Page Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <h1 style={{ fontSize: '24px', fontWeight: '700', color: 'var(--text-primary)', margin: 0 }}>
              📜 Di Chúc Tri Thức Số & Kế Thừa Di Sản
            </h1>
            <span style={{ fontSize: '11px', padding: '3px 8px', borderRadius: '12px', background: 'rgba(245, 158, 11, 0.15)', color: '#fbbf24', border: '1px solid rgba(245, 158, 11, 0.3)' }}>
              Vault Succession
            </span>
          </div>
          <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginTop: '6px', marginBottom: 0 }}>
            Đảm bảo di sản chất xám, tài liệu chuyên môn và tri thức cá nhân của bạn được chuyển giao an toàn cho thế hệ kế tiếp.
          </p>
        </div>

        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <button
            className="btn btn--secondary"
            onClick={handleExportVault}
            disabled={exporting}
            style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
          >
            {exporting ? (
              <>
                <div className="spinner" style={{ width: 14, height: 14 }} />
                <span>Đang tạo bản sao...</span>
              </>
            ) : (
              <>
                <span>📥 Xuất Bản Sao Thừa Kế (.JSON)</span>
              </>
            )}
          </button>

          <button
            className="btn btn--primary"
            onClick={handleSavePlan}
            disabled={saving}
            style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
          >
            {saving ? (
              <>
                <div className="spinner" style={{ width: 14, height: 14 }} />
                <span>Đang lưu...</span>
              </>
            ) : (
              <>
                <span>💾 Lưu Thiết Lập Di Chúc</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Main Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: '24px', marginBottom: '28px' }}>
        {/* Card 1: Succession Trigger Rules */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.02)',
          border: '1px solid var(--border)',
          borderRadius: '16px',
          padding: '24px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
            <span style={{ fontSize: '20px' }}>⏳</span>
            <div>
              <div style={{ fontSize: '15px', fontWeight: '700', color: 'var(--text-primary)' }}>
                Công Tắc Kích Hoạt Kế Thừa (Succession Triggers)
              </div>
              <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                Xác định điều kiện bàn giao dữ liệu tự động
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            {/* Mode: Inactivity Dead-man Switch */}
            <label style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '12px',
              padding: '12px 14px',
              borderRadius: '10px',
              background: plan.triggers.mode === 'inactivity' ? 'rgba(245, 158, 11, 0.08)' : 'rgba(255, 255, 255, 0.02)',
              border: plan.triggers.mode === 'inactivity' ? '1px solid rgba(245, 158, 11, 0.3)' : '1px solid rgba(255, 255, 255, 0.05)',
              cursor: 'pointer'
            }}>
              <input
                type="radio"
                name="trigger_mode"
                checked={plan.triggers.mode === 'inactivity'}
                onChange={() => setPlan({ ...plan, triggers: { ...plan.triggers, mode: 'inactivity' } })}
                style={{ marginTop: '3px' }}
              />
              <div style={{ width: '100%' }}>
                <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-primary)' }}>
                  ☠️ Công Tắc Tử Thần (Dead-Man's Switch)
                </div>
                <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>
                  Tự động chuyển giao nếu chủ tài khoản không đăng nhập trong thời gian chỉ định.
                </div>
                {plan.triggers.mode === 'inactivity' && (
                  <div style={{ marginTop: '10px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Thời gian chờ:</span>
                    <select
                      className="input"
                      value={plan.triggers.inactivity_days}
                      onChange={e => setPlan({ ...plan, triggers: { ...plan.triggers, inactivity_days: parseInt(e.target.value) } })}
                      style={{ padding: '4px 10px', fontSize: '12px' }}
                    >
                      <option value="60">60 ngày không hoạt động</option>
                      <option value="90">90 ngày không hoạt động</option>
                      <option value="180">180 ngày không hoạt động</option>
                      <option value="365">365 ngày (1 năm)</option>
                    </select>
                  </div>
                )}
              </div>
            </label>

            {/* Mode: Time-locked Date */}
            <label style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '12px',
              padding: '12px 14px',
              borderRadius: '10px',
              background: plan.triggers.mode === 'time_locked' ? 'rgba(56, 189, 248, 0.08)' : 'rgba(255, 255, 255, 0.02)',
              border: plan.triggers.mode === 'time_locked' ? '1px solid rgba(56, 189, 248, 0.3)' : '1px solid rgba(255, 255, 255, 0.05)',
              cursor: 'pointer'
            }}>
              <input
                type="radio"
                name="trigger_mode"
                checked={plan.triggers.mode === 'time_locked'}
                onChange={() => setPlan({ ...plan, triggers: { ...plan.triggers, mode: 'time_locked' } })}
                style={{ marginTop: '3px' }}
              />
              <div style={{ width: '100%' }}>
                <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-primary)' }}>
                  📅 Mở Khóa Theo Hẹn Ngày (Time-Locked Release)
                </div>
                <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>
                  Tri thức được bảo mật tuyệt đối và chỉ tự động mở vào ngày tương lai đã ấn định.
                </div>
                {plan.triggers.mode === 'time_locked' && (
                  <div style={{ marginTop: '10px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Ngày bàn giao:</span>
                    <input
                      type="date"
                      className="input"
                      value={plan.triggers.time_locked_date}
                      onChange={e => setPlan({ ...plan, triggers: { ...plan.triggers, time_locked_date: e.target.value } })}
                      style={{ padding: '4px 10px', fontSize: '12px' }}
                    />
                  </div>
                )}
              </div>
            </label>

            {/* Mode: Emergency Dual-Approval */}
            <label style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '12px',
              padding: '12px 14px',
              borderRadius: '10px',
              background: plan.triggers.mode === 'emergency' ? 'rgba(239, 68, 68, 0.08)' : 'rgba(255, 255, 255, 0.02)',
              border: plan.triggers.mode === 'emergency' ? '1px solid rgba(239, 68, 68, 0.3)' : '1px solid rgba(255, 255, 255, 0.05)',
              cursor: 'pointer'
            }}>
              <input
                type="radio"
                name="trigger_mode"
                checked={plan.triggers.mode === 'emergency'}
                onChange={() => setPlan({ ...plan, triggers: { ...plan.triggers, mode: 'emergency' } })}
                style={{ marginTop: '3px' }}
              />
              <div>
                <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-primary)' }}>
                  🚨 Kích Hoạt Khẩn Cấp (Dual-Guardian Approval)
                </div>
                <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>
                  Yêu cầu ít nhất 2 người thừa kế được chỉ định cùng gửi yêu cầu và duyệt mã OTP khẩn cấp.
                </div>
              </div>
            </label>
          </div>
        </div>

        {/* Card 2: Privacy Shield Settings */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.02)',
          border: '1px solid var(--border)',
          borderRadius: '16px',
          padding: '24px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
            <span style={{ fontSize: '20px' }}>🛡️</span>
            <div>
              <div style={{ fontSize: '15px', fontWeight: '700', color: 'var(--text-primary)' }}>
                Tấm Khiên Bảo Mật Di Sản (Privacy Shield)
              </div>
              <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                Lọc bỏ dữ liệu nhạy cảm trước khi trao quyền cho người kế thừa
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <label style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '12px',
              padding: '12px',
              borderRadius: '10px',
              background: 'rgba(255, 255, 255, 0.02)',
              border: '1px solid rgba(255, 255, 255, 0.05)',
              cursor: 'pointer'
            }}>
              <input
                type="checkbox"
                checked={plan.privacy_shield.anonymize_sensitive}
                onChange={e => setPlan({
                  ...plan,
                  privacy_shield: { ...plan.privacy_shield, anonymize_sensitive: e.target.checked }
                })}
                style={{ marginTop: '3px' }}
              />
              <div>
                <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-primary)' }}>
                  Tự Động Vô Danh Hóa Dữ Liệu Nhạy Cảm
                </div>
                <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>
                  Privacy Guard tự động che số CCCD/CMND, số tài khoản ngân hàng, số thẻ tín dụng trong các tài liệu bàn giao.
                </div>
              </div>
            </label>

            <label style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '12px',
              padding: '12px',
              borderRadius: '10px',
              background: 'rgba(255, 255, 255, 0.02)',
              border: '1px solid rgba(255, 255, 255, 0.05)',
              cursor: 'pointer'
            }}>
              <input
                type="checkbox"
                checked={plan.privacy_shield.exclude_financial_vaults}
                onChange={e => setPlan({
                  ...plan,
                  privacy_shield: { ...plan.privacy_shield, exclude_financial_vaults: e.target.checked }
                })}
                style={{ marginTop: '3px' }}
              />
              <div>
                <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-primary)' }}>
                  Loại Trừ Toàn Bộ Kho Báu Tài Chính Riêng Tư
                </div>
                <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>
                  Giữ lại các tài liệu chuyên môn, quy trình SOP, nhưng không chuyển giao các bảng kê tài chính cá nhân.
                </div>
              </div>
            </label>

            <label style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '12px',
              padding: '12px',
              borderRadius: '10px',
              background: 'rgba(255, 255, 255, 0.02)',
              border: '1px solid rgba(255, 255, 255, 0.05)',
              cursor: 'pointer'
            }}>
              <input
                type="checkbox"
                checked={plan.privacy_shield.strip_passwords_and_keys}
                onChange={e => setPlan({
                  ...plan,
                  privacy_shield: { ...plan.privacy_shield, strip_passwords_and_keys: e.target.checked }
                })}
                style={{ marginTop: '3px' }}
              />
              <div>
                <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-primary)' }}>
                  Tước Bỏ Mật Khẩu, Passkeys & Scoped API Tokens
                </div>
                <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>
                  Vô hiệu hóa toàn bộ access token bên thứ ba để đảm bảo người kế thừa không truy cập vào tài khoản ngoài ý muốn.
                </div>
              </div>
            </label>
          </div>
        </div>
      </div>

      {/* Beneficiaries Table Section */}
      <div style={{
        background: 'rgba(255, 255, 255, 0.02)',
        border: '1px solid var(--border)',
        borderRadius: '16px',
        padding: '24px'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <div style={{ fontSize: '16px', fontWeight: '700', color: 'var(--text-primary)' }}>
              👥 Danh Sách Người Thừa Kế Được Chỉ Định ({plan.beneficiaries.length})
            </div>
            <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>
              Những người sẽ được quyền tiếp quản hoặc kế thừa tri thức khi các điều kiện kích hoạt thỏa mãn
            </div>
          </div>

          <button
            className="btn btn--primary"
            onClick={() => setShowAddBenModal(true)}
            style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}
          >
            <span>➕ Thêm Người Thừa Kế</span>
          </button>
        </div>

        {plan.beneficiaries.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>
            Chưa có người thừa kế nào được thiết lập. Hãy thêm ít nhất 1 người giám hộ/kế thừa.
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)', color: 'var(--text-muted)', textAlign: 'left' }}>
                  <th style={{ padding: '12px 14px' }}>Họ Tên & Email</th>
                  <th style={{ padding: '12px 14px' }}>Mối Quan Hệ</th>
                  <th style={{ padding: '12px 14px' }}>Phân Quyền Thừa Kế</th>
                  <th style={{ padding: '12px 14px' }}>Trạng Thái Xác Thực</th>
                  <th style={{ padding: '12px 14px', textAlign: 'right' }}>Thao Tác</th>
                </tr>
              </thead>
              <tbody>
                {plan.beneficiaries.map(ben => (
                  <tr key={ben.id} style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.04)' }}>
                    <td style={{ padding: '14px' }}>
                      <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{ben.name}</div>
                      <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{ben.email}</div>
                    </td>
                    <td style={{ padding: '14px', color: 'var(--text-secondary)' }}>
                      {ben.relationship}
                    </td>
                    <td style={{ padding: '14px' }}>
                      {ben.role === 'full_access' ? (
                        <span style={{ fontSize: '11px', padding: '3px 8px', borderRadius: '6px', background: 'rgba(56, 189, 248, 0.15)', color: '#38bdf8', fontWeight: 600 }}>
                          👑 Toàn Quyền Tiếp Quản
                        </span>
                      ) : ben.role === 'professional_only' ? (
                        <span style={{ fontSize: '11px', padding: '3px 8px', borderRadius: '6px', background: 'rgba(168, 85, 247, 0.15)', color: '#c084fc', fontWeight: 600 }}>
                          💼 Chỉ Tri Thức Nghề Nghiệp
                        </span>
                      ) : (
                        <span style={{ fontSize: '11px', padding: '3px 8px', borderRadius: '6px', background: 'rgba(255, 255, 255, 0.08)', color: 'var(--text-muted)', fontWeight: 600 }}>
                          👁️ Chỉ Xem Lưu Trữ
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '14px' }}>
                      {ben.status === 'verified' ? (
                        <span style={{ fontSize: '11px', color: '#34d399', display: 'flex', alignItems: 'center', gap: '4px' }}>
                          ✓ Đã xác thực OTP
                        </span>
                      ) : (
                        <span style={{ fontSize: '11px', color: '#fbbf24', display: 'flex', alignItems: 'center', gap: '4px' }}>
                          ⏳ Chờ xác nhận
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '14px', textAlign: 'right' }}>
                      <button
                        className="btn btn--danger btn--sm"
                        onClick={() => handleRemoveBeneficiary(ben.id)}
                        title="Xóa người thừa kế"
                      >
                        🗑️
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal Add Beneficiary */}
      {showAddBenModal && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.75)',
          backdropFilter: 'blur(6px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            background: 'var(--bg-elevated)',
            border: '1px solid var(--border)',
            borderRadius: '16px',
            width: '100%',
            maxWidth: '480px',
            padding: '28px',
            boxShadow: '0 20px 50px rgba(0,0,0,0.5)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px' }}>
              <div style={{ fontSize: '18px', fontWeight: '700', color: 'var(--text-primary)' }}>
                ➕ Thêm Người Thừa Kế Tri Thức
              </div>
              <button
                onClick={() => setShowAddBenModal(false)}
                style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: '18px', cursor: 'pointer' }}
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleAddBeneficiary} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div>
                <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>
                  Họ và Tên Người Thừa Kế
                </label>
                <input
                  type="text"
                  className="input"
                  placeholder="VD: Nguyễn Minh Quân"
                  value={benForm.name}
                  onChange={e => setBenForm({ ...benForm, name: e.target.value })}
                  style={{ width: '100%' }}
                  required
                />
              </div>

              <div>
                <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>
                  Địa Chỉ Email Liên Hệ
                </label>
                <input
                  type="email"
                  className="input"
                  placeholder="quan.nguyen@family.vn"
                  value={benForm.email}
                  onChange={e => setBenForm({ ...benForm, email: e.target.value })}
                  style={{ width: '100%' }}
                  required
                />
              </div>

              <div>
                <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>
                  Mối Quan Hệ
                </label>
                <select
                  className="input"
                  value={benForm.relationship}
                  onChange={e => setBenForm({ ...benForm, relationship: e.target.value })}
                  style={{ width: '100%' }}
                >
                  <option value="Con ruột">Con ruột</option>
                  <option value="Vợ / Chồng">Vợ / Chồng</option>
                  <option value="Cộng sự ủy thác">Cộng sự ủy thác chuyên môn</option>
                  <option value="Luật sư đại diện">Luật sư đại diện pháp lý</option>
                  <option value="Cộng tác viên thân tín">Cộng tác viên thân tín</option>
                </select>
              </div>

              <div>
                <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>
                  Phân Quyền Kế Thừa
                </label>
                <select
                  className="input"
                  value={benForm.role}
                  onChange={e => setBenForm({ ...benForm, role: e.target.value as Beneficiary['role'] })}
                  style={{ width: '100%' }}
                >
                  <option value="full_access">👑 Toàn quyền tiếp quản (Mọi Vaults + Operations)</option>
                  <option value="professional_only">💼 Chỉ tri thức nghề nghiệp (Loại trừ tài chính riêng)</option>
                  <option value="view_only">👁️ Chỉ xem lưu trữ (Không có quyền sửa đổi)</option>
                </select>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '10px' }}>
                <button
                  type="button"
                  className="btn btn--secondary"
                  onClick={() => setShowAddBenModal(false)}
                >
                  Hủy
                </button>
                <button
                  type="submit"
                  className="btn btn--primary"
                >
                  Thêm Người Thừa Kế
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
